import { useState } from 'react';

import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { RiArrowRightSLine } from 'react-icons/ri';

import { IconEnvelopeCloseOutline, Link, SlideIn } from '@/components';
import {
  type CandidateBasicDto,
  type InviteCandidateResponse,
  SendInvitationChannel,
  type SmsTemplateDto,
} from '@/services/api';

import { CircularIconContainer } from '../../components/Icons/IconContainer';
import { SmsTemplateSelect } from '../../components/SmsTemplateSelect';
import {
  useCandidatesBulkInviteMutation,
  useInviteAllCandidatesRequest,
} from './useCandidateTableMutation';

interface SmsCandidatesConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId?: string;
  selectedCandidates: Array<CandidateBasicDto>;
}

export const SmsCandidatesConfirmationModal: React.FC<
  SmsCandidatesConfirmationModalProps
> = ({ isOpen, onClose, interviewId, selectedCandidates }) => {
  const toast = useToast();
  const { t } = useTranslation(['common', 'candidate']);
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<SmsTemplateDto['id']>();

  const count = selectedCandidates.length;

  const inviteAllCandidates = useInviteAllCandidatesRequest({
    onError() {},
    onSuccess() {
      onClose();
    },
  });

  const candidatesAdd = useCandidatesBulkInviteMutation({
    onSuccess() {
      onClose();
    },
  });

  const bulkSmsInviteHandler = async () => {
    let mutationPromise: Promise<InviteCandidateResponse>;
    if (interviewId) {
      mutationPromise = candidatesAdd.mutateAsync({
        candidateIds: selectedCandidates.map((c) => c.id),
        interviewId: interviewId,
        channel: SendInvitationChannel.Sms,
        smsTemplateId: selectedTemplateId,
      });
    } else {
      mutationPromise = inviteAllCandidates.mutateAsync({
        body: {
          candidateIds: selectedCandidates.map((c) => c.id),
          channel: SendInvitationChannel.Sms,
          smsTemplateId: selectedTemplateId,
        },
      });
    }

    toast.promise(mutationPromise, {
      loading: {
        title: t('candidate:inviteNewCandidate.smsSendingNotification', {
          count: selectedCandidates.length,
        }),
      },
      success: (response) => {
        const notInvitedCount = response.notInvitedCount ?? 0;
        const totalCount = response.totalCount ?? 0;

        if (notInvitedCount === 0) {
          return {
            title: t('candidate:inviteNewCandidate.smsSentNotification', {
              count: selectedCandidates.length,
            }),
          };
        }

        if (totalCount === 1 && notInvitedCount === 1) {
          return {
            title: t(
              'candidate:inviteNewCandidate.failSentNotificationForOneCandidate'
            ),
            status: 'warning',
          };
        }

        return {
          title: t(
            'candidate:inviteNewCandidate.failSentNotificationForCandidates'
          ),
          status: 'warning',
        };
      },
      error: {
        title: t('candidate:inviteNewCandidate.addCandidateError'),
      },
    });
  };

  return (
    <Modal
      isCentered
      size="md"
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />

        <VStack gap={0} w="100%" px={6}>
          <ModalHeader justifyItems="center" px={0}>
            <Flex justifyContent="center" mb={3}>
              <CircularIconContainer icon={IconEnvelopeCloseOutline} />
            </Flex>
            <Text mb={1} textAlign="center" fontSize="xl" fontWeight="semibold">
              {t('actions.sendSms')}
            </Text>
            <Text
              textAlign="center"
              fontSize="sm"
              fontWeight="semibold"
              color="gray.600"
            >
              {t('sms.selectTemplate')}
            </Text>
          </ModalHeader>

          <ModalBody w="full" pb={2} px={0}>
            <SlideIn>
              <SmsTemplateSelect
                selectedTemplateId={selectedTemplateId}
                setSelectedTemplateId={setSelectedTemplateId}
              />

              <Button
                onClick={bulkSmsInviteHandler}
                mb={4}
                mt={6}
                isLoading={candidatesAdd.isPending}
                variant="@primary"
                w="full"
              >
                {t('candidate:inviteNewCandidate.smsToCandidates', {
                  count,
                })}
              </Button>

              <Link to="/admin/settings/sms" mb={5}>
                <Button
                  rightIcon={<RiArrowRightSLine size="20px" />}
                  variant="link"
                  color="primary.500"
                  w="full"
                >
                  {t('sms.smsSettings')}
                </Button>
              </Link>
            </SlideIn>
          </ModalBody>
        </VStack>
      </ModalContent>
    </Modal>
  );
};
